'use client'

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { MarketImage } from '@/components/ui/market-image'
import { cn } from '@/lib/utils'
import { formatLiquidity, formatVolume } from '@/utils/formatting'
import { ClockIcon, Loader2Icon, SearchIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'
import {
	type RecentSearchItem,
	type SearchResultItem,
	useCommandPalette,
} from './use-command-palette'

type CommandPaletteProps = {
	isOpen: boolean
	onClose: () => void
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
	const {
		query,
		setQuery,
		groupedResults,
		showRecent,
		recentSearches,
		displayItems,
		selectedIndex,
		setSelectedIndex,
		isLoading,
		inputRef,
		handleKeyDown,
		navigateToItem,
	} = useCommandPalette(isOpen, onClose)

	// Track cumulative index for flattened list navigation
	let cumulativeIndex = 0

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
			<DialogContent
				className="flex max-h-[70vh] w-full max-w-[500px] flex-col gap-0 overflow-hidden border-zinc-800 bg-zinc-900 p-0"
				showCloseButton={false}
				onKeyDown={handleKeyDown}
			>
				<DialogTitle className="sr-only">Search markets and events</DialogTitle>

				{/* Search Input */}
				<div className="flex items-center gap-3 border-zinc-800 border-b px-4 py-3">
					<SearchIcon className="h-5 w-5 flex-shrink-0 text-zinc-400" />
					<input
						ref={inputRef}
						type="text"
						value={query}
						onChange={e => setQuery(e.target.value)}
						placeholder="Search markets and events..."
						className="flex-1 bg-transparent text-lg text-white outline-none placeholder:text-zinc-500"
						autoComplete="off"
						spellCheck={false}
					/>
					{isLoading && <Loader2Icon className="h-5 w-5 animate-spin text-zinc-400" />}
				</div>

				{/* Results */}
				<div className="flex-1 overflow-y-auto">
					{/* Recent Searches */}
					{showRecent && (
						<ResultSection title="Recent" icon={<ClockIcon className="h-4 w-4" />}>
							{recentSearches.map((item, idx) => {
								const itemIndex = idx
								return (
									<RecentItem
										key={`${item.platform}-${item.id}`}
										item={item}
										isSelected={selectedIndex === itemIndex}
										onSelect={() => navigateToItem(item)}
										onHover={() => setSelectedIndex(itemIndex)}
									/>
								)
							})}
						</ResultSection>
					)}

					{/* Search Results */}
					{query.trim() && groupedResults && (
						<>
							{groupedResults.events.length > 0 && (
								<ResultSection title="Events">
									{groupedResults.events.map((item, idx) => {
										const itemIndex = cumulativeIndex + idx
										return (
											<EventItem
												key={`${item.platform}-${item.id}`}
												item={item}
												isSelected={selectedIndex === itemIndex}
												onSelect={() => navigateToItem(item)}
												onHover={() => setSelectedIndex(itemIndex)}
											/>
										)
									})}
									{(() => {
										cumulativeIndex += groupedResults.events.length
										return null
									})()}
								</ResultSection>
							)}

							{groupedResults.markets.length > 0 && (
								<ResultSection title="Markets">
									{groupedResults.markets.map((item, idx) => {
										const itemIndex = cumulativeIndex + idx
										return (
											<MarketItem
												key={`${item.platform}-${item.id}`}
												item={item}
												isSelected={selectedIndex === itemIndex}
												onSelect={() => navigateToItem(item)}
												onHover={() => setSelectedIndex(itemIndex)}
											/>
										)
									})}
								</ResultSection>
							)}
						</>
					)}

					{/* Empty State */}
					{query.trim() && !isLoading && displayItems.length === 0 && (
						<div className="px-4 py-8 text-center text-zinc-500">
							No results found for "{query}"
						</div>
					)}

					{/* Initial State */}
					{!(query.trim() || showRecent) && (
						<div className="px-4 py-8 text-center text-zinc-500">
							Type to search markets and events
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="flex items-center justify-end gap-4 border-zinc-800 border-t px-4 py-2 text-xs text-zinc-500">
					<span className="flex items-center gap-1">
						<kbd className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono">↵</kbd>
						<span>Open</span>
					</span>
					<span className="flex items-center gap-1">
						<kbd className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono">↑↓</kbd>
						<span>Navigate</span>
					</span>
					<span className="flex items-center gap-1">
						<kbd className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono">esc</kbd>
						<span>Close</span>
					</span>
				</div>
			</DialogContent>
		</Dialog>
	)
}

function ResultSection({
	title,
	icon,
	children,
}: {
	title: string
	icon?: React.ReactNode
	children: React.ReactNode
}) {
	return (
		<div className="py-2">
			<div className="flex items-center gap-2 px-4 pb-2 font-medium text-xs text-zinc-500">
				{icon}
				{title}
			</div>
			{children}
		</div>
	)
}

function RecentItem({
	item,
	isSelected,
	onSelect,
	onHover,
}: {
	item: RecentSearchItem
	isSelected: boolean
	onSelect: () => void
	onHover: () => void
}) {
	const ref = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		if (isSelected && ref.current) {
			ref.current.scrollIntoView({ block: 'nearest' })
		}
	}, [isSelected])

	return (
		<button
			ref={ref}
			type="button"
			onClick={onSelect}
			onMouseEnter={onHover}
			className={cn(
				'flex w-full items-center gap-3 px-4 py-2 text-left transition-colors',
				isSelected ? 'bg-zinc-800' : 'hover:bg-zinc-800/50'
			)}
		>
			<MarketImage src={item.imageUrl} alt={item.title} title={item.title} size="sm" />
			<div className="min-w-0 flex-1">
				<div className="truncate text-sm text-white">{item.title}</div>
			</div>
			<span className="flex-shrink-0 text-xs text-zinc-500 capitalize">{item.type}</span>
		</button>
	)
}

function EventItem({
	item,
	isSelected,
	onSelect,
	onHover,
}: {
	item: SearchResultItem
	isSelected: boolean
	onSelect: () => void
	onHover: () => void
}) {
	const ref = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		if (isSelected && ref.current) {
			ref.current.scrollIntoView({ block: 'nearest' })
		}
	}, [isSelected])

	return (
		<button
			ref={ref}
			type="button"
			onClick={onSelect}
			onMouseEnter={onHover}
			className={cn(
				'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
				isSelected ? 'bg-zinc-800' : 'hover:bg-zinc-800/50'
			)}
		>
			<MarketImage src={item.imageUrl} alt={item.title} title={item.title} size="md" />
			<div className="min-w-0 flex-1">
				<div className="truncate font-medium text-sm text-white">{item.title}</div>
				<div className="mt-0.5 flex items-center gap-3 text-xs text-zinc-500">
					{item.marketCount !== undefined && <span>Markets {item.marketCount}</span>}
					{item.volume !== undefined && <span>Vol {formatVolume(item.volume)}</span>}
				</div>
			</div>
		</button>
	)
}

function MarketItem({
	item,
	isSelected,
	onSelect,
	onHover,
}: {
	item: SearchResultItem
	isSelected: boolean
	onSelect: () => void
	onHover: () => void
}) {
	const ref = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		if (isSelected && ref.current) {
			ref.current.scrollIntoView({ block: 'nearest' })
		}
	}, [isSelected])

	const yesPercent = item.yesPrice !== undefined ? (item.yesPrice * 100).toFixed(1) : null
	const noPercent = item.noPrice !== undefined ? (item.noPrice * 100).toFixed(1) : null

	return (
		<button
			ref={ref}
			type="button"
			onClick={onSelect}
			onMouseEnter={onHover}
			className={cn(
				'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
				isSelected ? 'bg-zinc-800' : 'hover:bg-zinc-800/50'
			)}
		>
			<MarketImage src={item.imageUrl} alt={item.title} title={item.title} size="md" />
			<div className="min-w-0 flex-1">
				<div className="line-clamp-2 font-medium text-sm text-white">{item.title}</div>
				<div className="mt-0.5 flex items-center gap-3 text-xs text-zinc-500">
					{item.volume !== undefined && <span>Vol {formatVolume(item.volume)}</span>}
					{item.liquidity !== undefined && <span>Liq {formatLiquidity(item.liquidity)}</span>}
				</div>
			</div>
			{yesPercent !== null && noPercent !== null && (
				<div className="flex flex-shrink-0 flex-col items-end text-xs">
					<span>
						<span className="text-emerald-400">Yes</span>{' '}
						<span className="text-white">{yesPercent}%</span>
					</span>
					<span>
						<span className="text-red-400">No</span>{' '}
						<span className="text-white">{noPercent}%</span>
					</span>
				</div>
			)}
		</button>
	)
}
